import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getNotifications, deleteNotification } from '../../services/notificationService';
import {
  getWebNotifications,
  deleteWebNotification
} from '../../services/webNotificationService';
import NotificationFormModal from './NotificationFormModal';
import WebNotificationFormModal from './WebNotificationFormModal';
import {
  Card, CardContent, Typography, Button, IconButton, Grid, Box, Chip,
  makeStyles, CircularProgress, Snackbar, Tooltip, TextField, InputAdornment,
  Tabs, Tab
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import RepeatIcon from '@material-ui/icons/Repeat';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import LaunchIcon from '@material-ui/icons/Launch';
import WebIcon from '@material-ui/icons/Web';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import EditIcon from '@material-ui/icons/Edit';
import PushPinIcon from '@material-ui/icons/PinDrop';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2.5),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  tabsContainer: {
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingText: {
    marginTop: 16,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  card: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
      borderColor: 'rgba(16,185,129,0.25)',
    },
  },
  cardImage: {
    width: '100%',
    height: 160,
    objectFit: 'cover',
    borderRadius: '16px 16px 0 0',
  },
  noImagePlaceholder: {
    width: '100%',
    height: 120,
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(30,41,59,0.7) 0%, rgba(15,23,42,0.7) 100%)'
      : 'linear-gradient(135deg, rgba(240,24df,245,1) 0%, rgba(245,245,245,1) 100%)',
    borderRadius: '16px 16px 0 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.disabled,
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  notificationType: {
    position: 'absolute',
    top: 12,
    right: 12,
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    backdropFilter: 'blur(4px)',
  },
  cardContent: {
    padding: '18px !important',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: 6,
    fontSize: '1.05rem',
    lineHeight: 1.4,
  },
  body: {
    color: theme.palette.text.secondary,
    marginBottom: 14,
    fontSize: '0.88rem',
    lineHeight: 1.5,
    flex: 1,
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: 10,
    marginTop: 'auto',
  },
  date: {
    color: theme.palette.text.secondary,
    fontSize: '0.78rem',
    fontWeight: 500,
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 6,
    '&:hover': {
      backgroundColor: 'rgba(56,189,248,0.1)',
      color: '#38bdf8',
    },
  },
  deleteBtn: {
    color: theme.palette.text.secondary,
    padding: 6,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  searchField: {
    minWidth: 260,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: theme.palette.background.paper,
    },
  },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  },
}));

export default function NotificationListPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  // Tab State: 0 = App Push, 1 = Web Notifications
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') === 'web' ? 1 : 0;
  const [activeTab, setActiveTab] = useState(initialTab);

  // App Notifications State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // Web Notifications State
  const [webItems, setWebItems] = useState([]);
  const [webLoading, setWebLoading] = useState(false);
  const [webSearch, setWebSearch] = useState('');

  // Confirm Delete Dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState('app'); // 'app' or 'web'
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Modal States
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushReuseData, setPushReuseData] = useState(null);
  const [webModalOpen, setWebModalOpen] = useState(false);
  const [webEditId, setWebEditId] = useState(null);
  const [webPrefillData, setWebPrefillData] = useState(null);

  const pageSize = 12;

  // Fetch Push Notifications
  const loadPushData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNotifications({
        page,
        pageSize,
        search,
        date: dateFilter,
      });
      setItems(data.items);
      setTotalPages(data.totalPages);
      setTotalCount(data.total);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load notifications' });
    }
    setLoading(false);
  }, [page, search, dateFilter]);

  // Fetch Web Notifications
  const loadWebData = useCallback(async () => {
    setWebLoading(true);
    try {
      const list = await getWebNotifications({ search: webSearch });
      setWebItems(list);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load web notifications' });
    }
    setWebLoading(false);
  }, [webSearch]);

  useEffect(() => {
    if (activeTab === 0) {
      loadPushData();
    } else {
      loadWebData();
    }
  }, [activeTab, loadPushData, loadWebData]);

  const handleDeleteClick = (id, type = 'app') => {
    setDeleteId(id);
    setDeleteType(type);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      if (deleteType === 'web') {
        await deleteWebNotification(deleteId);
        setSnackbar({ open: true, message: 'Web notification removed. It is no longer visible on Noor Web.' });
        loadWebData();
      } else {
        await deleteNotification(deleteId);
        setSnackbar({ open: true, message: 'Notification deleted successfully' });
        loadPushData();
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete notification' });
    }
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return '';
    const d = dateObj instanceof Date ? dateObj : new Date(dateObj);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AdminLayout title="Notifications Center">
      {/* Navigation Tabs */}
      <Box className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => {
            setActiveTab(val);
            history.replace(val === 1 ? '/notifications?tab=web' : '/notifications');
          }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            icon={<NotificationsActiveIcon fontSize="small" />}
            label="App Push Notifications"
            style={{ fontWeight: 600, textTransform: 'none', minHeight: 48 }}
          />
          <Tab
            icon={<WebIcon fontSize="small" />}
            label={`Web Notifications (${webItems.length})`}
            style={{ fontWeight: 600, textTransform: 'none', minHeight: 48 }}
          />
        </Tabs>
      </Box>

      {/* ================= TAB 0: APP PUSH NOTIFICATIONS ================= */}
      {activeTab === 0 && (
        <>
          <div className={classes.toolbar}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <TextField
                className={classes.searchField}
                placeholder="Search push notifications..."
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <TextField
                type="date"
                variant="outlined"
                size="small"
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  endAdornment: dateFilter ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setDateFilter('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>

            <Box display="flex" alignItems="center" style={{ gap: 16 }}>
              <Chip label={`Total: ${totalCount}`} className={classes.countChip} />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setPushReuseData(null);
                  setPushModalOpen(true);
                }}
                style={{
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                }}
              >
                Send Notification
              </Button>
            </Box>
          </div>

          {loading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress size={44} style={{ color: '#10b981' }} />
              <Typography className={classes.loadingText}>Loading notifications...</Typography>
            </div>
          ) : items.length === 0 ? (
            <div className={classes.loadingContainer}>
              <NotificationsActiveIcon style={{ fontSize: 48, color: '#94a3b8', marginBottom: 12 }} />
              <Typography variant="h6" color="textSecondary" style={{ fontWeight: 600 }}>
                No notifications found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Send a notification to keep your app users updated.
              </Typography>
            </div>
          ) : (
            <Grid container spacing={3}>
              {items.map((n) => (
                <Grid item xs={12} sm={6} md={4} key={n.id}>
                  <Card className={classes.card}>
                    {n.imageUrl ? (
                      <img
                        src={n.imageUrl}
                        alt={n.title}
                        className={classes.cardImage}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className={classes.noImagePlaceholder}>
                        <span>Text Notification</span>
                      </div>
                    )}

                    <Chip
                      label={n.status === 'scheduled' ? 'Scheduled' : 'Sent'}
                      size="small"
                      className={classes.notificationType}
                      style={{
                        background: n.status === 'scheduled' ? '#f59e0b' : '#10b981',
                        color: '#fff',
                      }}
                    />

                    <CardContent className={classes.cardContent}>
                      <Typography className={classes.title} noWrap title={n.title}>
                        {n.title}
                      </Typography>
                      <Typography className={classes.body}>
                        {n.body && n.body.length > 110 ? `${n.body.substring(0, 110)}...` : n.body}
                      </Typography>

                      <div className={classes.meta}>
                        <Typography className={classes.date}>
                          {n.status === 'scheduled' && n.scheduledAt
                            ? `Scheduled: ${formatDate(n.scheduledAt)}`
                            : formatDate(n.createdAt)}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Tooltip title="Resend / Reuse Content">
                            <IconButton
                              className={classes.actionBtn}
                              size="small"
                              onClick={() => {
                                setPushReuseData(n);
                                setPushModalOpen(true);
                              }}
                            >
                              <RepeatIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Publish as Web Notification">
                            <IconButton
                              className={classes.actionBtn}
                              size="small"
                              style={{ color: '#10b981' }}
                              onClick={() => {
                                setWebEditId(null);
                                setWebPrefillData(n);
                                setWebModalOpen(true);
                              }}
                            >
                              <WebIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              className={classes.deleteBtn}
                              size="small"
                              onClick={() => handleDeleteClick(n.id, 'app')}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {!loading && items.length > 0 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(p) => setPage(p)}
            />
          )}
        </>
      )}

      {/* ================= TAB 1: WEB NOTIFICATIONS ================= */}
      {activeTab === 1 && (
        <>
          <div className={classes.toolbar}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <TextField
                className={classes.searchField}
                placeholder="Search web notifications..."
                variant="outlined"
                size="small"
                value={webSearch}
                onChange={(e) => setWebSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: webSearch ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setWebSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>

            <Box display="flex" alignItems="center" style={{ gap: 16 }}>
              <Chip label={`Active Web: ${webItems.length}`} className={classes.countChip} />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setWebEditId(null);
                  setWebPrefillData(null);
                  setWebModalOpen(true);
                }}
                style={{
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                }}
              >
                Create Web Notification
              </Button>
            </Box>
          </div>

          <Box
            p={2}
            mb={3}
            borderRadius={12}
            bgcolor="rgba(56,189,248,0.08)"
            border="1px solid rgba(56,189,248,0.2)"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <WebIcon style={{ color: '#38bdf8' }} />
              <Typography variant="body2" style={{ color: '#38bdf8', fontWeight: 500 }}>
                These notifications are displayed in the <strong>Noor Web</strong> notification drawer. They will be retrieved by web visitors until you delete them here.
              </Typography>
            </Box>
          </Box>

          {webLoading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress size={44} style={{ color: '#10b981' }} />
              <Typography className={classes.loadingText}>Loading web notifications...</Typography>
            </div>
          ) : webItems.length === 0 ? (
            <div className={classes.loadingContainer}>
              <WebIcon style={{ fontSize: 48, color: '#94a3b8', marginBottom: 12 }} />
              <Typography variant="h6" color="textSecondary" style={{ fontWeight: 600 }}>
                No active web notifications
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Create a web notification or choose "Yes, Store for Web" when sending a push notification.
              </Typography>
            </div>
          ) : (
            <Grid container spacing={3}>
              {webItems.map((n) => (
                <Grid item xs={12} sm={6} md={4} key={n.id}>
                  <Card className={classes.card}>
                    {n.imageUrl ? (
                      <img
                        src={n.imageUrl}
                        alt={n.title}
                        className={classes.cardImage}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className={classes.noImagePlaceholder}>
                        <span>Web Announcement</span>
                      </div>
                    )}

                    <Chip
                      label={n.category || 'Announcement'}
                      size="small"
                      className={classes.notificationType}
                      style={{
                        background: '#0284c7',
                        color: '#fff',
                      }}
                    />

                    <CardContent className={classes.cardContent}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        {n.isPinned && (
                          <Chip
                            size="small"
                            label="Pinned"
                            style={{
                              background: 'rgba(245,158,11,0.15)',
                              color: '#f59e0b',
                              fontWeight: 700,
                              height: 22,
                            }}
                          />
                        )}
                        <Typography className={classes.title} noWrap title={n.title}>
                          {n.title}
                        </Typography>
                      </Box>

                      <Typography className={classes.body}>
                        {n.summary && n.summary.length > 100 ? `${n.summary.substring(0, 100)}...` : n.summary}
                      </Typography>

                      {n.content && (
                        <Typography variant="caption" color="textSecondary" style={{ fontStyle: 'italic', marginBottom: 8 }}>
                          Includes extended article content
                        </Typography>
                      )}

                      <div className={classes.meta}>
                        <Typography className={classes.date}>
                          {formatDate(n.createdAt)}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Tooltip title="Edit Web Notification">
                            <IconButton
                              className={classes.actionBtn}
                              size="small"
                              onClick={() => {
                                setWebEditId(n.id);
                                setWebPrefillData(null);
                                setWebModalOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete from Noor Web">
                            <IconButton
                              className={classes.deleteBtn}
                              size="small"
                              onClick={() => handleDeleteClick(n.id, 'web')}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmOpen}
        title={deleteType === 'web' ? 'Delete Web Notification?' : 'Delete Notification?'}
        message={
          deleteType === 'web'
            ? 'Are you sure you want to delete this web notification? It will immediately stop appearing on Noor Web.'
            : 'Are you sure you want to delete this notification record?'
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />

      <NotificationFormModal
        open={pushModalOpen}
        initialData={pushReuseData}
        onClose={() => setPushModalOpen(false)}
        onSuccess={() => {
          setPushModalOpen(false);
          loadPushData();
        }}
        onOpenWebNotification={(data) => {
          setWebEditId(null);
          setWebPrefillData(data);
          setWebModalOpen(true);
        }}
      />

      <WebNotificationFormModal
        open={webModalOpen}
        notificationId={webEditId}
        prefillData={webPrefillData}
        onClose={() => setWebModalOpen(false)}
        onSuccess={() => {
          setWebModalOpen(false);
          loadWebData();
        }}
      />
    </AdminLayout>
  );
}